import React from "react";
import Dropdown from "@/components/ui/Dropdown";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  Building2,
  Briefcase,
  GitBranch,
  RefreshCw,
  Calendar,
  Filter,
  HelpCircle,
  MoreVertical,
  ChevronDown,
} from "lucide-react";

const RecruiterAdminFilters = ({
  userRole,
  companyOptions,
  selectedCompanies,
  onCompanyChange,
  onResetCompany,
  companyDisabled,
  campaignOptions,
  selectedCampaigns,
  onCampaignChange,
  onResetCampaign,
  loadingCampaigns,
  vacancyOptions,
  vacanciesCount,
  selectedVacancies,
  onVacancyChange,
  onResetVacancy,
  loadingVacancies,
  stageOptions,
  stagesCount,
  selectedStages,
  onStageChange,
  onResetStage,
  loadingStages,
  rejectedAtOptions,
  selectedRejectionReasons,
  onRejectionChange,
  onResetRejectedAt,
  showRejectedAt,
  selectedDateRange = [null, null],
  onDateRangeChange,
  onResetDateRange,
  onApplyFilters,
  loadingDashboard = false,
}) => {
  const hasDateSelected = selectedDateRange?.[0] || selectedDateRange?.[1];

  return (
    <div className="flex flex-col gap-6">
      {/* Primary Selection Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* Select Company Card */}
        <div className="bg-card rounded-xl border border-border shadow-sm p-6 space-y-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-1.5 font-semibold text-muted-foreground">
              Select Company <span className="text-primary">*</span>
              <HelpCircle className="w-4 h-4 text-muted-foreground cursor-help" />
            </div>
            <MoreVertical className="w-5 h-5 text-muted-foreground cursor-pointer" />
          </div>

          <div className="flex items-center gap-2">
            <Dropdown
              options={companyOptions}
              selectedValues={selectedCompanies.map((c) => ({
                label: c.name,
                value: c.id,
              }))}
              onChange={onCompanyChange}
              placeholder="Select Company"
              icon={
                <div className="w-2 h-2 rounded-full bg-green-500 mr-2 shrink-0" />
              }
              searchable
              multiSelect
              className="w-full border border-border rounded-lg h-11"
              dropdownWidth="w-full"
              renderOption={(option) => (
                <p className="text-foreground">{option.label}</p>
              )}
              allowAll={false}
              disabled={companyDisabled}
            />
            {selectedCompanies.length > 0 && (
              <button
                type="button"
                className="p-2 rounded-lg hover:bg-muted focus:outline-none transition-colors shrink-0"
                onClick={onResetCompany}
                title="Reset Company"
              >
                <RefreshCw className="w-4 h-4 text-muted-foreground" />
              </button>
            )}
          </div>
        </div>

        {/* Date Range Card */}
        <div className="bg-card rounded-xl border border-border shadow-sm p-6 space-y-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-1.5 font-semibold text-muted-foreground">
              Date Range <span className="text-primary">*</span>
              <HelpCircle className="w-4 h-4 text-muted-foreground cursor-help" />
            </div>
            <MoreVertical className="w-5 h-5 text-muted-foreground cursor-pointer" />
          </div>

          <div className="flex items-center gap-2">
            <div className="relative w-full">
              <DatePicker
                selected={selectedDateRange?.[0]}
                startDate={selectedDateRange?.[0]}
                endDate={selectedDateRange?.[1]}
                selectsRange
                onChange={onDateRangeChange}
                placeholderText="Select Dates"
                dateFormat="dd/MM/yyyy"
                className="w-full h-11 pl-10 pr-4 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary bg-card transition-all"
                calendarClassName="react-datepicker-custom"
                autoComplete="off"
              />
              <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none flex items-center">
                <div className="w-2 h-2 rounded-full bg-green-500 mr-2 shrink-0" />
              </div>
              <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                <ChevronDown className="w-5 h-5 text-muted-foreground" />
              </div>
            </div>
            {hasDateSelected && onResetDateRange && (
              <button
                type="button"
                className="p-2 rounded-lg hover:bg-muted focus:outline-none transition-colors shrink-0"
                onClick={onResetDateRange}
                title="Clear date range"
              >
                <RefreshCw className="w-4 h-4 text-muted-foreground" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Secondary Filters Section */}
      {(selectedCompanies.length > 0 || selectedCampaigns.length > 0) && (
        <div className="bg-card p-6 rounded-xl border border-border shadow-sm flex flex-wrap gap-6 items-end">
          {selectedCompanies.length > 0 && (
            <div className="flex flex-col min-w-[240px] flex-1 max-w-sm">
              <label className="text-sm font-semibold text-muted-foreground mb-2">
                Campaign
              </label>
              <div className="flex items-center gap-2">
                <Dropdown
                  options={campaignOptions}
                  selectedValues={selectedCampaigns}
                  onChange={onCampaignChange}
                  placeholder={
                    loadingCampaigns ? "Loading..." : "Select Campaigns"
                  }
                  icon={
                    <div className="w-2 h-2 rounded-full bg-green-500 mr-2 shrink-0" />
                  }
                  searchable
                  multiSelect
                  className="w-full border border-border rounded-lg h-11"
                  dropdownWidth="w-full"
                  renderOption={(option) => (
                    <p className="text-foreground">{option.label}</p>
                  )}
                  allowAll={false}
                  disabled={loadingCampaigns}
                />
                {selectedCampaigns.length > 0 && (
                  <button
                    type="button"
                    className="p-2 rounded-lg hover:bg-muted focus:outline-none transition-all"
                    onClick={onResetCampaign}
                    title="Reset Campaign"
                  >
                    <RefreshCw className="w-4 h-4 text-muted-foreground" />
                  </button>
                )}
              </div>
            </div>
          )}

          {selectedCampaigns.length > 0 && (
            <div className="flex flex-col min-w-[240px] flex-1 max-w-sm">
              <label className="text-sm font-semibold text-muted-foreground mb-2">
                Vacancy
              </label>
              <div className="flex items-center gap-2">
                <Dropdown
                  options={
                    vacanciesCount > 0
                      ? vacancyOptions
                      : [
                          {
                            label: "No Vacancies",
                            value: "no_vacancies",
                            disabled: true,
                          },
                        ]
                  }
                  selectedValues={selectedVacancies}
                  onChange={onVacancyChange}
                  placeholder={
                    loadingVacancies
                      ? "Loading..."
                      : vacanciesCount > 0
                        ? "Select Vacancies"
                        : "No Vacancies"
                  }
                  icon={
                    <div className="w-2 h-2 rounded-full bg-green-500 mr-2 shrink-0" />
                  }
                  searchable
                  multiSelect
                  className="w-full border border-border rounded-lg h-11"
                  dropdownWidth="w-full"
                  renderOption={(option) => (
                    <p
                      className={`text-foreground ${option.disabled ? "opacity-50" : ""}`}
                    >
                      {option.label}
                    </p>
                  )}
                  allowAll={false}
                  disabled={loadingVacancies || vacanciesCount === 0}
                />
                {selectedVacancies.length > 0 && (
                  <button
                    type="button"
                    className="p-2 rounded-lg hover:bg-muted focus:outline-none transition-all"
                    onClick={onResetVacancy}
                    title="Reset Vacancy"
                  >
                    <RefreshCw className="w-4 h-4 text-muted-foreground" />
                  </button>
                )}
              </div>
            </div>
          )}

          {selectedVacancies.length > 0 && (
            <div className="flex flex-col min-w-[240px] flex-1 max-w-sm">
              <label className="text-sm font-semibold text-muted-foreground mb-2">
                Stage
              </label>
              <div className="flex items-center gap-2">
                <Dropdown
                  options={
                    stagesCount > 0
                      ? stageOptions
                      : [
                          {
                            label: "No Stages",
                            value: "no_stages",
                            disabled: true,
                          },
                        ]
                  }
                  selectedValues={selectedStages}
                  onChange={onStageChange}
                  placeholder={
                    loadingStages
                      ? "Loading..."
                      : stagesCount > 0
                        ? "Select Stages"
                        : "No Stages"
                  }
                  icon={
                    <div className="w-2 h-2 rounded-full bg-green-500 mr-2 shrink-0" />
                  }
                  searchable
                  multiSelect={selectedVacancies.length === 1}
                  className="w-full border border-border rounded-lg h-11"
                  dropdownWidth="w-full"
                  renderOption={(option) => (
                    <p
                      className={`text-foreground ${option.disabled ? "opacity-50" : ""}`}
                    >
                      {option.label}
                    </p>
                  )}
                  allowAll={false}
                  disabled={loadingStages || stagesCount === 0}
                />
                {selectedStages.length > 0 && (
                  <button
                    type="button"
                    className="p-2 rounded-lg hover:bg-muted focus:outline-none transition-all"
                    onClick={onResetStage}
                    title="Reset Stage"
                  >
                    <RefreshCw className="w-4 h-4 text-muted-foreground" />
                  </button>
                )}
              </div>
            </div>
          )}

          {selectedVacancies.length > 0 && showRejectedAt && (
            <div className="flex flex-col min-w-[240px] flex-1 max-w-sm">
              <label className="text-sm font-semibold text-muted-foreground mb-2">
                Rejected at
              </label>
              <div className="flex items-center gap-2">
                <Dropdown
                  options={
                    rejectedAtOptions.length > 0
                      ? rejectedAtOptions
                      : [
                          {
                            label: "No Stages",
                            value: "no_stages",
                            disabled: true,
                          },
                        ]
                  }
                  selectedValues={selectedRejectionReasons}
                  onChange={onRejectionChange}
                  placeholder="Select Rejected at"
                  icon={
                    <div className="w-2 h-2 rounded-full bg-green-500 mr-2 shrink-0" />
                  }
                  searchable
                  multiSelect={false}
                  className="w-full border border-border rounded-lg h-11"
                  dropdownWidth="w-full"
                  renderOption={(option) => (
                    <p
                      className={`text-foreground ${option.disabled ? "opacity-50" : ""}`}
                    >
                      {option.label}
                    </p>
                  )}
                  allowAll={false}
                />
                {selectedRejectionReasons.length > 0 && (
                  <button
                    type="button"
                    className="p-2 rounded-lg hover:bg-muted focus:outline-none transition-all"
                    onClick={onResetRejectedAt}
                    title="Reset Rejected at"
                  >
                    <RefreshCw className="w-4 h-4 text-muted-foreground" />
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Apply Button in Secondary Row */}
          {onApplyFilters && (
            <div className="flex-1 flex justify-end">
              <button
                type="button"
                className="inline-flex items-center gap-2 px-6 py-2.5 h-[36px] text-sm font-semibold text-white bg-primary-dark rounded-lg hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 transition-all shadow-sm"
                onClick={onApplyFilters}
                disabled={loadingDashboard}
              >
                <Filter className="w-4 h-4" />
                {loadingDashboard ? "Loading..." : "Apply Filters"}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Separate Apply Button if no secondary row exists */}
      {onApplyFilters &&
        selectedCompanies.length === 0 &&
        !selectedCampaigns.length && (
          <div className="flex justify-end">
            <button
              type="button"
              className="inline-flex items-center gap-2 px-6 py-2.5 h-[36px] text-sm font-semibold text-white bg-primary-dark rounded-lg hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 transition-all shadow-sm"
              onClick={onApplyFilters}
              disabled={loadingDashboard}
            >
              <Filter className="w-4 h-4" />
              {loadingDashboard ? "Loading..." : "Apply Filters"}
            </button>
          </div>
        )}
    </div>
  );
};

export default RecruiterAdminFilters;
